import { useState, useEffect } from "react";
import { Switch, IconButton } from "@mui/material";
import {
  Variable,
  VariableType,
  VariableScope,
} from "../../../models/interactions/Variable";
import PiInputfield from "../../../components/PiInputField";
import PiButton from "../../../components/PiButton";
import VariableTypeSelector from "./VariableTypeSelector";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import FunctionsIcon from "@mui/icons-material/Functions";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import VariableContextMenu from "./VariableContextMenu";
import {
  getBuiltInFriendlyLabel,
  isBuiltInVariable,
  isBuiltInVariableName,
} from "../../../models/interactions/BuiltInSequenceVariables";

interface VariableItemProps {
  variable: Variable;
  isEditing: boolean;
  onEdit: () => void;
  onSave: (variable: Variable) => void;
  onCancel: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  existingVariables: Variable[];
}

export default function VariableItem({
  variable,
  isEditing,
  onEdit,
  onSave,
  onCancel,
  onDelete,
  onDuplicate,
  existingVariables,
}: VariableItemProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editedName, setEditedName] = useState(variable.name);
  const [stringValue, setStringValue] = useState("");
  const [numberValue, setNumberValue] = useState("");
  const [booleanValue, setBooleanValue] = useState(false);
  const builtIn = isBuiltInVariable(variable);

  // Initialize values based on variable type when editing starts
  useEffect(() => {
    if (isEditing) {
      setEditedName(variable.name);
      if (variable.type === VariableType.Boolean) {
        setBooleanValue(variable.value as boolean);
      } else if (variable.type === VariableType.Number) {
        setNumberValue(variable.value.toString());
      } else {
        setStringValue(variable.value.toString());
      }
    }
  }, [isEditing, variable]);

  const handleSave = () => {
    // Validate unique name
    const nameExists = existingVariables.some(
      (v) => v.name === editedName.trim() && v.id !== variable.id
    );
    if (nameExists) {
      alert("Variable name must be unique");
      return;
    }

    if (!editedName.trim()) {
      alert("Variable name is required");
      return;
    }

    if (isBuiltInVariableName(editedName.trim())) {
      alert("This variable name is reserved for a built-in variable");
      return;
    }

    let value: boolean | string | number;
    if (variable.type === VariableType.Boolean) {
      value = booleanValue;
    } else if (variable.type === VariableType.Number) {
      const num = Number(numberValue);
      if (isNaN(num) || numberValue.trim() === "") {
        alert("Please enter a valid number");
        return;
      }
      value = num;
    } else {
      if (stringValue.trim() === "") {
        alert("Please enter a value");
        return;
      }
      value = stringValue;
    }

    onSave({
      ...variable,
      name: editedName.trim(),
      type: variable.type, // Keep original type - cannot be changed
      value: value,
      scope: VariableScope.Public, // Always Public for now
    });
  };

  const isFormValid = () => {
    if (editedName.trim().length === 0) return false;
    if (
      isBuiltInVariableName(editedName.trim()) &&
      editedName.trim() !== variable.name
    ) {
      return false;
    }
    if (variable.type === VariableType.Boolean) {
      return true; // Boolean always has a value
    } else if (variable.type === VariableType.Number) {
      const num = Number(numberValue);
      return !isNaN(num) && numberValue.trim() !== "";
    } else {
      return stringValue.trim().length > 0;
    }
  };

  const getTypeIcon = () => {
    if (builtIn) {
      return (
        <LockOutlinedIcon
          sx={{ fontSize: 20 }}
          className="text-creator-primary"
        />
      );
    }

    switch (variable.type) {
      case VariableType.String:
        return (
          <TextFieldsIcon
            sx={{ fontSize: 20 }}
            className="text-creator-primary"
          />
        );
      case VariableType.Number:
        return (
          <FunctionsIcon
            sx={{ fontSize: 20 }}
            className="text-creator-primary"
          />
        );
      case VariableType.Boolean:
        return (
          <ToggleOnIcon
            sx={{ fontSize: 20 }}
            className="text-creator-primary"
          />
        );
      case VariableType.BuiltIn:
        return (
          <LockOutlinedIcon
            sx={{ fontSize: 20 }}
            className="text-creator-primary"
          />
        );
    }
  };

  const getTypeLabel = () => {
    switch (variable.type) {
      case VariableType.String:
        return "String";
      case VariableType.Number:
        return "Number";
      case VariableType.Boolean:
        return "Boolean";
      case VariableType.BuiltIn:
        return "Built-in";
    }
  };

  const formatValue = () => {
    if (variable.type === VariableType.Boolean) {
      return variable.value ? "True" : "False";
    }
    return variable.value.toString();
  };

  const renderValueInput = () => {
    switch (variable.type) {
      case VariableType.Boolean:
        return (
          <div className="flex gap-2 items-center">
            <Switch
              checked={booleanValue}
              onChange={(event, checked) => setBooleanValue(checked)}
            />
            <span className="text-[12px] text-creator-text-sub font-poppins">
              {booleanValue ? "True" : "False"}
            </span>
          </div>
        );
      case VariableType.Number:
        return (
          <PiInputfield
            label=""
            placeholder="0"
            value={numberValue}
            onChange={setNumberValue}
            square
          />
        );
      case VariableType.String:
      case VariableType.BuiltIn:
        return (
          <PiInputfield
            label=""
            placeholder="Enter text"
            value={stringValue}
            onChange={setStringValue}
            square
          />
        );
    }
  };

  if (isEditing) {
    if (builtIn) {
      return null;
    }

    return (
      <div className="flex flex-col gap-4 p-2 w-full h-full font-poppins">
        {/* Variable Name */}
        <div className="flex flex-col gap-2">
          <label className="text-[12px] font-medium text-creator-text-sub tracking-wide font-poppins">
            Variable Name
          </label>
          <PiInputfield
            label=""
            placeholder="e.g. scoreCounter"
            value={editedName}
            onChange={setEditedName}
            autoFocus
            square
          />
        </div>

        {/* Variable Type - Read Only */}
        <div className="flex flex-col gap-2">
          <label className="text-[12px] font-medium text-creator-text-sub tracking-wide font-poppins">
            Variable Type
          </label>
          <div className="opacity-60 pointer-events-none">
            <VariableTypeSelector
              selectedType={variable.type}
              onSelect={() => {}} // Disabled - cannot change type
            />
          </div>
        </div>

        {/* Initial Value */}
        <div className="flex flex-col gap-2">
          <label className="text-[12px] font-medium text-creator-text-sub tracking-wide font-poppins">
            Initial Value
          </label>
          {renderValueInput()}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end mt-auto">
          <div className="w-[120px] h-[36px]">
            <PiButton variant="piBlueOutlined" onClick={onCancel}>
              Cancel
            </PiButton>
          </div>
          <div className="w-[120px] h-[36px]">
            <PiButton
              variant="piBlue"
              onClick={handleSave}
              disabled={!isFormValid()}
            >
              Confirm
            </PiButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 py-2 px-3 bg-transparent rounded-[8px] font-poppins hover:bg-creator-primary-background/50 transition-all duration-200">
      {/* Type Icon */}
      <div className="flex flex-shrink-0 justify-center items-center w-8 h-8">
        {getTypeIcon()}
      </div>

      {/* Variable Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="text-[14px] font-medium text-creator-text-sub font-poppins truncate">
            {builtIn ? getBuiltInFriendlyLabel(variable.name) : variable.name}
          </div>
        </div>
        <div className="text-[12px] text-creator-text-soft font-poppins truncate">
          {builtIn
            ? `Key: ${variable.name} | ${getTypeLabel()}`
            : `${getTypeLabel()} | ${formatValue()}`}
        </div>
      </div>

      {/* Options Menu */}
      <div className="flex flex-shrink-0 items-center">
        {!builtIn && (
          <IconButton
            aria-label="variable-options-menu"
            className="w-[30px] h-[30px]"
            onClick={(event) => {
              event.preventDefault();
              setAnchorEl(event.currentTarget);
              setMenuOpen(true);
            }}
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        )}
        <VariableContextMenu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={() => {
            setMenuOpen(false);
            setAnchorEl(null);
          }}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
          canEdit={!builtIn}
          canDuplicate={!builtIn}
          canDelete={!builtIn}
        />
      </div>
    </div>
  );
}
